import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'react-toastify';

const Login = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const { login } = useAuth();
  const location = useLocation();

  // Display message if redirected from session expiration
  useEffect(() => {
    const message = location.state?.message;
    if (message) {
      toast.info(message);
    }
  }, [location]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      await login(username, password);
      toast.success('Login successful');
    } catch (err) {
      console.error('Login error:', err);
      const status = err.response?.status;
      // The server deliberately does not say which of the two was wrong.
      const message =
        status === 401 ? 'Incorrect username or password. Please try again.'
        : status === 403 ? (err.response?.data?.error || 'This account has been deactivated.')
        : err.response?.data?.error
          || (err.response ? 'Could not sign in. Please try again.'
                           : 'Cannot reach the server. Check your connection and try again.');
      setError(message);
      toast.error(message);
      setPassword('');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      <div className="flex-grow flex items-center justify-center bg-gray-100">
        <div className="max-w-md w-full p-6 bg-white rounded-lg shadow-lg">
          <div className="text-center mb-8">
            <img 
              src="/logo.png" 
              alt="Company Logo" 
              className="mx-auto h-16 mb-4"
            />
            <h1 className="text-2xl font-bold text-gray-900">VoIP SMS App</h1>
            <p className="text-gray-600">Sign in to your account</p>
          </div>

          {error && (
            <div
              role="alert"
              aria-live="assertive"
              className="mb-4 flex items-start gap-2 rounded-md border border-red-300 bg-red-50 px-3 py-2"
            >
              <span aria-hidden="true" className="text-red-600 font-bold leading-5">!</span>
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label htmlFor="username" className="block text-sm font-medium text-gray-700">
                Username
              </label>
              <input
                id="username"
                type="text"
                value={username}
                onChange={(e) => { setUsername(e.target.value); if (error) setError(''); }}
                className={`mt-1 block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 ${
                  error ? 'border-red-400' : 'border-gray-300'
                }`}
                required
              />
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                Password
              </label>
              <input
                id="password"
                type="password"
                value={password}
                onChange={(e) => { setPassword(e.target.value); if (error) setError(''); }}
                className={`mt-1 block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 ${
                  error ? 'border-red-400' : 'border-gray-300'
                }`}
                required
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className={`w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 ${
                loading ? 'opacity-50 cursor-not-allowed' : ''
              }`}
            >
              {loading ? (
                <div className="flex items-center">
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                  Signing in...
                </div>
              ) : (
                'Sign in'
              )}
            </button>
          </form>
        </div>
      </div>
      <footer className="bg-white py-4 text-center text-gray-600 shadow-inner">
        <a 
          href="https://TechnoTechSolutions.net" 
          target="_blank" 
          rel="noopener noreferrer"
          className="hover:text-blue-600 transition-colors duration-200"
        >
          Designed and developed by TechnoTechSolutions.net
        </a>
      </footer>
    </div>
  );
};

export default Login;
